import type { AppRole, Permission } from "@/lib/auth/rbac-config";
import {
  PERMISSION_SEEDS,
  PERMISSION_SEEDS_WITH_SPA_KEYS,
  permissionCode,
} from "./permissions.seed";

function backendCodesForSpaPermissions(
  spaPermissions: readonly Permission[],
): string[] {
  const codes = new Set<string>();

  for (const seed of PERMISSION_SEEDS_WITH_SPA_KEYS) {
    if (seed.spaKey && spaPermissions.includes(seed.spaKey)) {
      codes.add(permissionCode(seed));
    }
  }

  return [...codes];
}

/** Profile permissions (no SPA key) granted to all non-admin roles that use the SPA. */
const PROFILE_PERMISSION_CODES = ["profile:read"] as const;

const VIEWER_SPA_PERMISSIONS = [
  "view:dashboard",
  "view:history",
  "view:alerts",
  "view:screenshots",
] as const satisfies readonly Permission[];

const REVIEWER_SPA_PERMISSIONS = [
  "view:history",
  "view:screenshots",
  "view:alerts",
  "view:masters",
  "create:masters",
  "update:masters",
  "delete:masters",
  "view:reports",
  "download:reports",
  "create:equipment",
  "update:equipment",
  "delete:equipment",
  "create:intervention-categories",
  "update:intervention-categories",
  "delete:intervention-categories",
  "create:interventions",
  "update:interventions",
  "delete:interventions",
  "review:screenshots",
  "review:alerts",
] as const satisfies readonly Permission[];

const AUDITOR_SPA_PERMISSIONS = [
  "view:audit-trail",
] as const satisfies readonly Permission[];

/** Default backend permission codes assigned to each built-in role (DB seeding). */
export const ROLE_PERMISSION_CODES: Record<AppRole, readonly string[]> = {
  ADMIN: PERMISSION_SEEDS.map(permissionCode),

  VIEWER: [
    ...backendCodesForSpaPermissions(VIEWER_SPA_PERMISSIONS),
    ...PROFILE_PERMISSION_CODES,
  ],

  REVIEWER: [
    ...backendCodesForSpaPermissions(REVIEWER_SPA_PERMISSIONS),
    ...PROFILE_PERMISSION_CODES,
  ],

  AUDITOR: [
    ...backendCodesForSpaPermissions(AUDITOR_SPA_PERMISSIONS),
    ...PROFILE_PERMISSION_CODES,
  ],
};
